import { useState, useEffect, useCallback } from "react";
import { Tabs } from "../components/shared/tabs";
import { Pill } from "../components/shared/pill";
import { Spinner } from "../components/shared/spinner";
import { useMerchantStore } from "../store/merchant";
import { useCheckoutStore } from "../store/checkout";
import { apiGet, apiPut, endpoints } from "../api/client";
import type { MerchantSettings } from "../api/types";
import { ItemGrid } from "../components/pos/item-grid";
import { CartPanel } from "../components/pos/cart-panel";
import { CheckoutFlow } from "../components/pos/checkout-flow";
import { SettingsForm } from "../components/settings/settings-form";
import { SquareConnection } from "../components/settings/square-connection";
import { InflatePanel } from "../components/inventory/inflate-panel";
import { PairFlow } from "../components/terminal/pair-flow";
import { AppBar } from "../components/shared/app-bar";
import "./dashboard.css";

const DASHBOARD_TABS = [
  { id: "pos", label: "POS" },
  { id: "inventory", label: "Inventory" },
  { id: "log", label: "Log" },
  { id: "terminal", label: "Terminal" },
  { id: "settings", label: "Settings" },
];

const DEVICE_STORAGE_KEY = "scd-terminal-device";

export function DashboardPage() {
  const [activeTab, setActiveTab] = useState("pos");
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceId, setDeviceId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DEVICE_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const { appMerchantId } = useMerchantStore();
  const setMerchant = useMerchantStore((s) => s.setMerchant);
  const startChoosing = useCheckoutStore((s) => s.startChoosing);
  const [squareError, setSquareError] = useState<string | null>(null);

  // Handle OAuth callback redirect from the API:
  //   success → /dashboard?appMerchantId=...&businessName=...
  //   failure → /dashboard?squareError=<message>
  // Read once on mount, apply to store / banner, then strip params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("squareError");
    const newAppMerchantId = params.get("appMerchantId");
    const businessName = params.get("businessName");

    if (!err && !newAppMerchantId) return;

    if (err) {
      setSquareError(err);
    } else if (newAppMerchantId) {
      setMerchant({
        app_merchant_id: newAppMerchantId,
        business_name: businessName ?? "",
        connected: true,
      });
    }

    params.delete("squareError");
    params.delete("appMerchantId");
    params.delete("businessName");
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`,
    );
  }, [setMerchant]);

  const loadSettings = useCallback(async () => {
    try {
      const data = await apiGet<MerchantSettings>(endpoints.settings);
      setSettings(data);
    } catch {
      // Settings will be null on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (appMerchantId) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [appMerchantId, loadSettings]);

  const mode = settings?.mode ?? 1;

  async function switchMode() {
    if (!settings) return;
    const newMode = mode === 1 ? 2 : 1;
    const updated = { ...settings, mode: newMode as 1 | 2 };
    await apiPut(endpoints.settings, updated);
    setSettings(updated);
  }

  function handlePaired(id: string) {
    setDeviceId(id);
    try {
      localStorage.setItem(DEVICE_STORAGE_KEY, id);
    } catch {
      // Ignore storage errors
    }
  }

  function handleUnpaired() {
    setDeviceId(null);
    try {
      localStorage.removeItem(DEVICE_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
  }

  if (loading) {
    return (
      <div style={{ padding: "64px 0", textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }

  const needsSquareConnection = !appMerchantId;

  return (
    <>
      {squareError && (
        <div
          role="alert"
          style={{
            margin: "12px 16px 0",
            padding: "12px 14px",
            background: "var(--red-bg)",
            border: "1px solid var(--red-bd)",
            color: "var(--red)",
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
          }}
        >
          <span style={{ flex: 1 }}>
            Square connection failed: {squareError}
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setSquareError(null)}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--red)",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>
      )}

      {/* Top bar */}
      <AppBar
        variant="dashboard"
        pills={
          !needsSquareConnection ? (
            <>
              <Pill variant={mode === 1 ? "orange" : "blue"}>Mode {mode}</Pill>
              <Pill variant={deviceId ? "green" : "orange"}>
                {deviceId ? "Terminal" : "No Terminal"}
              </Pill>
            </>
          ) : null
        }
      />

      {!needsSquareConnection && (
        <div className={`mode-banner mode${mode}`}>
          <span>
            {mode === 1
              ? "Mode 1: True Cash Discount — prices adjust at checkout"
              : "Mode 2: Inflated Inventory — cash customers get discount"}
          </span>
          <button className="mode-switch-btn" onClick={switchMode}>
            Switch
          </button>
        </div>
      )}

      {/* Tabs — always show so user can reach Settings to connect Square */}
      <Tabs
        tabs={needsSquareConnection
          ? DASHBOARD_TABS.filter((t) => t.id === "settings")
          : DASHBOARD_TABS
        }
        activeId={needsSquareConnection ? "settings" : activeTab}
        onChange={setActiveTab}
      />

      {/* Tab content */}
      <div>
        {activeTab === "pos" && !needsSquareConnection && (
          <>
            <div className="pos-layout">
              {/* pos-items wraps ItemGrid (which renders a fragment of search + items)
                  into a single grid cell so the cart can occupy the second column. */}
              <div className="pos-items">
                <ItemGrid />
              </div>
              <div className="pos-cart">
                <CartPanel onCharge={startChoosing} />
              </div>
            </div>
            {settings && (
              <CheckoutFlow settings={settings} deviceId={deviceId} />
            )}
          </>
        )}
        {activeTab === "inventory" && !needsSquareConnection && settings && (
          <InflatePanel settings={settings} onSettingsChange={loadSettings} />
        )}
        {activeTab === "log" && !needsSquareConnection && (
          <div style={{ padding: "48px 16px", textAlign: "center" }}>
            <div style={{ fontSize: "36px", marginBottom: "10px" }}>&#x1F9FE;</div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--ink2)" }}>
              No transactions yet
            </div>
            <div style={{ fontSize: "12px", color: "var(--ink3)", marginTop: "4px" }}>
              Completed sales appear here
            </div>
          </div>
        )}
        {activeTab === "terminal" && !needsSquareConnection && (
          <PairFlow
            deviceId={deviceId}
            onPaired={handlePaired}
            onUnpaired={handleUnpaired}
          />
        )}
        {(activeTab === "settings" || needsSquareConnection) && (
          <div>
            {needsSquareConnection && (
              <div style={{ padding: "16px 16px 0", textAlign: "center" }}>
                <div style={{ fontSize: "36px", marginBottom: "12px" }}>&#x1F517;</div>
                <div style={{ fontSize: "17px", fontWeight: 800, marginBottom: "8px" }}>
                  Connect Square to get started
                </div>
              </div>
            )}
            {!needsSquareConnection && (
              <SettingsForm onModeChange={(m) => {
                if (settings) setSettings({ ...settings, mode: m });
              }} />
            )}
            <div className="settings-connection">
              <SquareConnection />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
